import type { Vec2 } from '../../geo/types';
import type { VertexStyle } from '../mesh/meshBuilder';
import { PATTERN } from '../../render/patterns/patternIds';
import type { Opening } from './openings';
import { LEADED_LIGHT, type UnitLight } from './openingDetail';

/**
 * Saddle bars across each leaded light: early casements had "two, three or more in each light"
 * (buildingconservation.com, "Early Casement Window Furniture"); no Amsterdam source counts them.
 */
export const SADDLE_BARS = 2;

/** A leaded light in wall coordinates: its lower-left corner and its size, in metres. */
export interface WallLight {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

function placed(light: UnitLight, x: number, y: number, width: number, height: number): WallLight {
  return { x: x + light.x * width, y: y + light.y * height, width: light.width * width, height: light.height * height };
}

/**
 * The leaded light of an opening's infill slice, or null where the slice is not glazed: a cross
 * window's upper slice (its two upper lights, the left one's corner given) and a door's transom light.
 */
export function leadedLight(o: Opening, slice: number): WallLight | null {
  if (slice !== 1) return null;
  if (o.kind === 'window') return placed(LEADED_LIGHT.window, o.x, o.y, o.width, o.height);
  if (o.kind === 'door' && o.leaf !== undefined && o.leaf < o.height) {
    return placed(LEADED_LIGHT.doorTransom, o.x, o.y + o.leaf, o.width, o.height - o.leaf);
  }
  return null;
}

/** The glass drawn as leaded lights (STYLE §15.1): the light's width, and a saddle bar every panel. */
export function leadedStyle(glass: VertexStyle, light: WallLight): VertexStyle {
  return { ...glass, pattern: PATTERN.leadedGlass, extent: [light.width, light.height / (SADDLE_BARS + 1)] };
}

/** A point of the wall in the light's coordinates: metres right of and above its lower-left corner. */
export function lightUv(light: WallLight, x: number, y: number): Vec2 {
  return [x - light.x, y - light.y];
}
